import { useEffect, useRef } from 'react'
import { EVENTOS, type PreferenciasUsuario } from '@crm/shared'
import { pegarSocket, soltarSocket } from './socket'

/**
 * O toque de mensagem recebida (ADR 0040).
 *
 * Questionário de 03/10/2026: só para as conversas da pessoa, um toque curto
 * e discreto, e a fila com um toque diferente, porém parecido. Sem aviso do
 * Windows. Quem deve ouvir o servidor já decidiu (`quem-ouve-o-som.ts`);
 * aqui fica o que só o navegador sabe: se a pessoa desligou o som e se ela já
 * está olhando para aquela conversa.
 *
 * Os toques são sintetizados na hora, sem arquivo de áudio: dois sinos
 * curtos, a mesma voz nos dois. O das minhas sobe (lá, mi), o da fila desce
 * (mi, lá). Mesma família, para soar como o mesmo sistema; direção oposta,
 * para dar para saber qual é sem olhar.
 */

export type Toque = 'minha' | 'fila'

const NOTAS: Record<Toque, [number, number]> = {
  minha: [880, 1318.5],
  fila: [1318.5, 880],
}

/** Várias mensagens em sequência viram um toque só. */
const INTERVALO_MINIMO_MS = 2500

let contexto: AudioContext | null = null

function audio(): AudioContext | null {
  if (typeof window === 'undefined' || !('AudioContext' in window)) return null
  contexto ??= new AudioContext()
  return contexto
}

/*
  O navegador só deixa tocar som depois de a pessoa interagir com a página.
  O primeiro clique ou tecla destrava; depois de recarregar a tela, até esse
  primeiro clique, o toque fica mudo. É regra do navegador, não escolha.
*/
function destravarNoPrimeiroGesto() {
  const destravar = () => {
    void audio()?.resume()
    window.removeEventListener('pointerdown', destravar)
    window.removeEventListener('keydown', destravar)
  }
  window.addEventListener('pointerdown', destravar)
  window.addEventListener('keydown', destravar)
}
if (typeof window !== 'undefined') destravarNoPrimeiroGesto()

function sino(ctx: AudioContext, frequencia: number, inicio: number, volume: number) {
  const oscilador = ctx.createOscillator()
  const ganho = ctx.createGain()
  oscilador.type = 'sine'
  oscilador.frequency.value = frequencia
  // Ataque rápido e queda exponencial: soa como sino, e não como bipe.
  ganho.gain.setValueAtTime(0.0001, inicio)
  ganho.gain.exponentialRampToValueAtTime(Math.max(0.0002, 0.3 * volume), inicio + 0.012)
  ganho.gain.exponentialRampToValueAtTime(0.0001, inicio + 0.28)
  oscilador.connect(ganho).connect(ctx.destination)
  oscilador.start(inicio)
  oscilador.stop(inicio + 0.3)
}

/** Toca agora. Usado também pelo botão "ouvir" do menu, para testar o volume. */
export function tocarToque(toque: Toque, volume: number): void {
  const ctx = audio()
  if (!ctx || volume <= 0) return
  void ctx.resume()
  const agora = ctx.currentTime + 0.01
  const [primeira, segunda] = NOTAS[toque]
  sino(ctx, primeira, agora, volume)
  sino(ctx, segunda, agora + 0.11, volume)
}

/*
  A conversa aberta no bate-papo. Mensagem nela, com a aba à vista, não toca:
  a pessoa está lendo, e o som seria só ruído (é o que o WhatsApp faz).
*/
let conversaAberta: string | null = null

export function marcarConversaAberta(chamadoId: string | null): void {
  conversaAberta = chamadoId
}

/**
 * Duas abas do sistema abertas tocariam duas vezes o mesmo toque. A trava do
 * navegador (Web Locks) é dividida entre as abas: a primeira que pegar toca,
 * e segura a trava pelo intervalo mínimo, que vale para todas.
 */
async function tocarSemRepetir(toque: Toque, volume: number) {
  const tocar = () => tocarToque(toque, volume)
  if (!('locks' in navigator)) {
    tocar()
    return
  }
  await navigator.locks.request(`crm-som-${toque}`, { ifAvailable: true }, async (trava) => {
    if (!trava) return
    tocar()
    await new Promise((fim) => setTimeout(fim, INTERVALO_MINIMO_MS))
  })
}

export function useSomDasMensagens(preferencias: PreferenciasUsuario | undefined) {
  // A preferência muda pelo menu sem reabrir o socket: lida na hora do toque.
  const atual = useRef(preferencias)
  atual.current = preferencias

  useEffect(() => {
    const socket = pegarSocket()

    const aoReceber = (dados: unknown) => {
      const { toque, chamadoId } = (dados ?? {}) as { toque?: Toque; chamadoId?: string }
      const prefs = atual.current
      if (!prefs || (toque !== 'minha' && toque !== 'fila')) return
      if (toque === 'minha' && !prefs.somMinhas) return
      if (toque === 'fila' && !prefs.somFila) return
      if (document.visibilityState === 'visible' && chamadoId && chamadoId === conversaAberta) {
        return
      }
      void tocarSemRepetir(toque, prefs.volumeSom)
    }

    socket.on(EVENTOS.somDeMensagem, aoReceber)
    return () => {
      socket.off(EVENTOS.somDeMensagem, aoReceber)
      soltarSocket()
    }
  }, [])
}
